/**
 * The tracker sheet, as a screen.
 *
 * One row per *submission*, not per placement. The workbook tracked a person
 * from the moment they were put forward to a client, and a row that only
 * appears once somebody has joined cannot be the sheet the recruiters worked
 * from — the rows they chase hardest are the ones with no joining date yet.
 *
 * Columns in the workbook's own order: Sr-No, Recruiter, Month, Candidate,
 * Mobile, Client, Location, Position, DOI, DOJ, CTC, Status, Remarks.
 *
 * Status is derived by the server across submission, interview, offer and
 * placement — see `app/services/tracker.py` for the ladder. Nothing here
 * recomputes it, because a status derived twice is a status that disagrees
 * with itself.
 *
 * Two columns fill in only once an offer is accepted: DOJ and CTC live on the
 * placement. A row with neither is not incomplete, it is early.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Loader2, Pencil, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { client as api, errorMessage } from '../api/client';
import ImportPanel from '../components/ImportPanel';
import { downloadExport, exportErrorMessage } from '../lib/exportDownload';
import {
  EARNED_INCENTIVE_LABEL,
  TENTATIVE_INCENTIVE_LABEL,
  hasValue,
} from '../lib/incentive';
import { money } from '../lib/money';
import { formatPhone } from '../lib/phone';
import { isCompanyAdmin, isSupervisor, useAuth } from '../store/auth';
import { titleCaseName } from '../lib/titleCase';
import { monthLabel, sourcingFeeLabel } from '../lib/trackerColumns';
import {
  INTERVIEW_STATUSES,
  TRACKER_INTERVIEW_STATUSES,
  formatDayMonthYear,
  interviewStatusLabel,
} from '../lib/candidateOptions';
import RecruiterFilter from '../components/RecruiterFilter';
import SearchSelect from '../components/SearchSelect';
import ClientSelect from '../components/ClientSelect';
import type {
  Client,
  FeeBasis,
  Page,
  PaymentState,
  PlacementStatus,
  TrackerRow,
  TrackerStatus,
} from '../types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];


/** Six years back is further than this data goes and cheap to offer. */
const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

/**
 * Row fill by status, matching the Excel export's scheme.
 *
 * Taken from STATUS_FILLS in `xlsx_export.py` — grouped by what the reader does
 * next rather than by which enum the value came from. Green is progressing,
 * amber is waiting on somebody, red is closed and the answer was no.
 *
 * The five rungs the export did not have when it was written are coloured on
 * the same rule and added there in the same change, so the sheet and the file
 * stay one scheme: an offer out is progress, an interview not yet held is
 * waiting, and a refused offer is closed.
 *
 * This deliberately disagrees with the badge palette on Placements, which
 * colours `hold` grey. The export calls it amber, because a hold is somebody
 * still to come back to rather than a dead end, and the brief for this screen
 * was to match the export. Placements keeps its own scheme.
 *
 * Colour is never the only signal — Status stays in its own column, which is
 * what keeps the screen readable to the ~8% of men with red/green colour
 * blindness.
 */
/** Four words for the money, and colour is never the only signal. */
const PAYMENT_LABEL: Record<PaymentState, string> = {
  not_invoiced: 'Yet to pay',
  yet_to_receive: 'Invoice done',
  part_paid: 'Awaiting payment',
  received: 'Payment received',
  // Its own word. A withdrawn claim is not money that is late, and calling it
  // awaited would have the tracker wait for something nobody will send.
  cancelled: 'Cancelled',
};

/**
 * What an administrator may *assert*, out of the five above.
 *
 * Three, not five, and the two that are missing are missing for different
 * reasons. `not_invoiced` is a fact about whether a document exists in this
 * system, which is not an opinion anybody gets to hold; `cancelled` is a
 * decision about a document, with its own route and its own consequences on
 * the billing screens. Asserting either here would have the tracker contradict
 * the invoice about whether anybody is owed anything.
 *
 * The same list the server's validator holds, so the dropdown cannot offer a
 * choice the API will refuse.
 */
const SETTABLE_PAYMENT_STATES: PaymentState[] = [
  'yet_to_receive',
  'part_paid',
  'received',
];

/**
 * The dropdown's own wording, which is the client's.
 *
 * Deliberately not PAYMENT_LABEL above. That one captions a *derived* badge
 * and is worded from the invoice's point of view -- 'Invoice done' is a
 * sensible thing to read off a raised invoice and a strange thing to choose
 * from a menu. Here somebody is answering "what happened to this fee", and
 * these are the three words they use for it.
 */
const PAYMENT_CHOICE_LABEL: Record<PaymentState, string> = {
  not_invoiced: 'Not invoiced',
  yet_to_receive: 'Not yet paid',
  part_paid: 'Partially paid',
  received: 'Received',
  cancelled: 'Cancelled',
};

/** A dotted ring, so an asserted badge is visibly not a computed one. */
const PAYMENT_OVERRIDDEN_RING = 'ring-1 ring-dashed ring-gray-400';

const PAYMENT_TONE: Record<PaymentState, string> = {
  // Grey, not amber. Nothing is owed yet, so nothing is waiting.
  not_invoiced: 'bg-gray-100 text-gray-500',
  yet_to_receive: 'bg-amber-100 text-amber-800',
  // Amber too: part of it is still outstanding, and that is the fact worth
  // seeing. The label is what separates the two.
  part_paid: 'bg-amber-100 text-amber-800',
  received: 'bg-emerald-100 text-emerald-700',
  // A withdrawn claim is not money that is late; it is money nobody will send.
  cancelled: 'bg-gray-100 text-gray-400',
};

const ROW_TONE: Record<TrackerStatus, string> = {
  // Progressing.
  shortlisted: 'bg-emerald-50',
  interviewed: 'bg-emerald-50',
  selected: 'bg-emerald-50',
  offered: 'bg-emerald-50',
  working: 'bg-emerald-50',
  // Waiting on somebody.
  pending: 'bg-amber-50',
  interview_scheduled: 'bg-amber-50',
  followup: 'bg-amber-50',
  hold: 'bg-amber-50',
  yet_to_join: 'bg-amber-50',
  // Closed, and the answer was no.
  rejected: 'bg-red-50',
  offer_declined: 'bg-red-50',
  offer_withdrawn: 'bg-red-50',
  drop: 'bg-red-50',
  not_joined: 'bg-red-50',
  // Closed, and the answer was yes in the end. A drop we replaced kept the
  // fee, so colouring it with the drops would read as a loss it was not.
  replacement_done: 'bg-emerald-50',
};

/**
 * No column is frozen any more.
 *
 * The two FROZEN_* class strings that stood here described a sticky Candidate
 * column and stopped being referenced when that column was unfrozen. They
 * were left behind, and `noUnusedLocals` in tsconfig.app.json turns an unused
 * const into a build failure rather than a warning — so the leftovers broke
 * the production build while every local check passed.
 */

const STATUS_TEXT: Record<TrackerStatus, string> = {
  shortlisted: 'text-emerald-800',
  interviewed: 'text-emerald-800',
  selected: 'text-emerald-800',
  offered: 'text-emerald-800',
  working: 'text-emerald-800',
  pending: 'text-amber-800',
  interview_scheduled: 'text-amber-800',
  followup: 'text-amber-800',
  hold: 'text-amber-800',
  yet_to_join: 'text-amber-800',
  rejected: 'text-red-800',
  offer_declined: 'text-red-800',
  offer_withdrawn: 'text-red-800',
  drop: 'text-red-800',
  not_joined: 'text-red-800',
  replacement_done: 'text-emerald-800',
};

/** Every rung of the ladder, in funnel order not alphabetical. */
/**
 * The statuses a row on this screen can actually have.
 *
 * Shorter than TrackerStatus by design: the tracker lists candidates from the
 * client's yes onward, so pending, shortlisted, interviewed, followup, hold
 * and rejected cannot appear here. Offering them would be a filter that can
 * only ever return an empty table, which reads as "no results" rather than
 * "not on this screen". They are all still on Interview Candidates.
 */
const TRACKER_STATUSES: TrackerStatus[] = [
  'selected',
  'offered',
  'offer_declined',
  'offer_withdrawn',
  'yet_to_join',
  'working',
  'drop',
  'not_joined',
  'replacement_done',
];

/**
 * Rows per page. The endpoint caps limit at 200; fifty matches the calls
 * report and is the number the paging is built around.
 */
const PAGE_SIZE = 50;

/**
 * The submission statuses a person may set, matching the Interviews screen.
 *
 * Not every SubmissionStatus: `yet_to_join` and `working` are what an accepted
 * offer and a placement produce, and offering them here would let somebody
 * type a state the pipeline is supposed to arrive at. `followup` is set by the
 * outcome flow rather than chosen.
 */
const SETTABLE_SUBMISSION_STATUSES = [
  'pending',
  'shortlisted',
  'selected',
  'hold',
  'rejected',
];

/** What a placement's own status may be set to, which is a shorter list. */
const PLACEMENT_STATUSES: PlacementStatus[] = [
  'yet_to_join',
  'working',
  'hold',
  'not_joined',
  'drop',
  'rejected',
];

/** An empty cell that reads as empty rather than as a missing column. */
function Blank() {
  return <span className="text-gray-300">—</span>;
}

/** '' from an empty input means "no value", which the API spells as null. */
function orNull(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

function label(status: string): string {
  return status.replace(/_/g, ' ');
}

/**
 * Correct the placement behind a tracker row.
 *
 * Only offered on rows that have one. Every field here lives on the placement,
 * so on a row that has not got that far there is nothing for this to write to
 * — the control is absent rather than present-and-failing.
 *
 * No role gate. PATCH /placements is scoped by visibility rather than by role:
 * a recruiter may correct their own rows, a manager their subtree. That
 * matches the offer flow, where accepting an offer — the act that creates the
 * placement in the first place — is open to any signed-in user. Requiring a
 * manager to fix a row would be stricter than making it.
 *
 * Remarks is here too, and is the one field that does not live on the
 * placement — it is the supervisor's note on the submission, still editable in
 * place on the row. Having it in both is the point: a manager fixing a row
 * should not have to close this and go hunting for one column.
 *
 * It is also the one field a recruiter cannot write. The pencil is gated on a
 * row having a placement rather than on rank, so a recruiter can open this and
 * correct their own placement fields — but the remarks route is Approver, so
 * for them this shows the manager's note and refuses to be typed in. A field
 * that accepts text and then 403s on save would be worse than one that says
 * plainly it is not theirs.
 */
function EditDialog({
  row,
  saving,
  canEditRemarks,
  mayEditAll,
  onCancel,
  onSave,
}: {
  row: TrackerRow;
  saving: boolean;
  canEditRemarks: boolean;
  /**
   * Whether this viewer may correct the whole row rather than the placement.
   *
   * Company Admin and Super Admin. A tracker row is assembled from four
   * records -- the candidate, the submission, the offer and the placement --
   * and until now this dialog could only write to one of them, so a row whose
   * joining date had been typed on the candidate was uncorrectable from the
   * screen that showed it. Admins get the other two fields that are writable;
   * everybody else keeps exactly what they had.
   */
  mayEditAll: boolean;
  onCancel: () => void;
  onSave: (
    payload: Record<string, unknown> | null,
    remarks: { changed: boolean; value: string },
    extras: {
      candidate: Record<string, unknown> | null;
      submission: Record<string, unknown> | null;
      /**
       * What an administrator says about the fee, when they changed it.
       *
       * Its own entry rather than a field on `payload`, because it is its own
       * route with its own gate: the placement PATCH is open to whoever can
       * see the row, and this is admin-and-owner like the supervisor's note.
       * Folding it into the placement body would have handed a recruiter the
       * one field on this dialog that moves money.
       *
       * `null` inside a `{ value }` wrapper means "clear the assertion and
       * read the invoice again", which a bare null could not say -- that is
       * already what "unchanged" looks like.
       */
      payment: { value: PaymentState | null } | null;
    },
  ) => void;
}) {
  const [remarks, setRemarks] = useState(row.trackerRemarks ?? '');
  const [position, setPosition] = useState(row.position ?? '');
  const [location, setLocation] = useState(row.location ?? '');
  const [dateOfInterview, setDateOfInterview] = useState(row.dateOfInterview ?? '');
  const [dateOfJoining, setDateOfJoining] = useState(row.dateOfJoining ?? '');
  const [ctcAnnual, setCtcAnnual] = useState(row.ctcAnnual ?? '');
  const [billingAmount, setBillingAmount] = useState(row.billingAmount ?? '');
  const [feeBasis, setFeeBasis] = useState<FeeBasis | ''>(row.feeBasis ?? '');
  const [feePercent, setFeePercent] = useState(row.feePercent ?? '');
  const [guaranteeUntil, setGuaranteeUntil] = useState(row.guaranteeUntil ?? '');
  /**
   * Why the candidate left. The placement's own remarks, not the supervisor's.
   *
   * Saved with the rest of the placement rather than through the remarks
   * route, which is Approver-only -- a recruiter has to be able to write this
   * one, since they are the person who found out.
   */
  const [whyLeft, setWhyLeft] = useState(
    row.placementRemarks ?? row.submissionRemarks ?? '',
  );
  /** The client's employee number, which prints on their invoice. */
  const [employeeRef, setEmployeeRef] = useState(row.clientEmployeeRef ?? '');
  /**
   * What an administrator says happened to the fee.
   *
   * Seeded from the *assertion*, not from the effective state. Those differ on
   * every row nobody has ruled on, and seeding from the effective one would
   * make simply opening and saving a dialog freeze today's derived answer into
   * a permanent assertion -- the invoice could then be paid and the tracker
   * would go on saying what it said the day somebody happened to look.
   */
  const [paymentState, setPaymentState] = useState<PaymentState | ''>(
    row.paymentStateOverride ?? '',
  );

  /**
   * The client's standing terms, so the blanks can be offered rather than
   * calculated by hand.
   *
   * Fetched when the dialog opens instead of being carried on every tracker
   * row: one client's terms are the same for all their rows, and putting four
   * more columns on a paged list to serve one dialog is the wrong trade.
   */
  const client = useQuery<Client>({
    queryKey: ['client', row.clientId],
    queryFn: async () => (await api.get(`/api/v1/clients/${row.clientId}`)).data,
    staleTime: 5 * 60_000,
  });

  /**
   * What the client's terms say this placement's fee would be.
   *
   * A *suggestion*, and it is important that it is only ever that. Nothing
   * writes it: it fills the box, the person looking at the box decides, and
   * pressing Save is what makes it the agreed fee. That is the line
   * offer_service draws — "a copied-but-stale figure is worse than a blank
   * one because it looks settled" — and it holds here because a human is
   * looking at a form, which is exactly what is absent when a placement is
   * created by accepting an offer.
   *
   * Null when there is nothing honest to suggest: no standing terms, or a
   * percentage basis with no CTC to take a percentage of. An empty box beats
   * a figure derived from a blank.
   */
  const suggestedFee = (() => {
    const c = client.data;
    if (!c || billingAmount.trim() !== '') return null;
    if (c.defaultFeeBasis === 'flat') return c.defaultBillingAmount;
    if (c.defaultFeeBasis === 'ctc_percent' && c.defaultFeePercent) {
      const ctc = Number(ctcAnnual);
      const pct = Number(c.defaultFeePercent);
      if (!Number.isFinite(ctc) || ctc <= 0 || !Number.isFinite(pct)) return null;
      return ((ctc * pct) / 100).toFixed(2);
    }
    return null;
  })();

  /** The guarantee window the client's replacement period implies. */
  const suggestedGuarantee = (() => {
    const days = client.data?.defaultReplacementDays;
    if (!days || guaranteeUntil.trim() !== '' || dateOfJoining.trim() === '') {
      return null;
    }
    const joined = new Date(dateOfJoining);
    if (Number.isNaN(joined.getTime())) return null;
    return new Date(joined.getTime() + days * 86_400_000)
      .toISOString()
      .slice(0, 10);
  })();
  // Safe to narrow: on a placement-backed row the derived status is the
  // placement's own, which is always one of the six below.
  const [status, setStatus] = useState<PlacementStatus>(
    row.status as PlacementStatus,
  );

  const statusChanged = status !== row.status;

  /**
   * The three fields that live outside the placement.
   *
   * Two on the candidate -- what the recruiter recorded and the joining date
   * they were given -- and one on the submission, the role it was put forward
   * for. Each is sent to its own endpoint, and only when it changed: an
   * untouched dialog stays one request, and a request that changes nothing is
   * a write nobody asked for.
   */
  const [clientId, setClientId] = useState(row.clientId);
  /**
   * The submission's own status, for a row with no placement.
   *
   * The Status column is derived, and where no placement exists it is reading
   * the submission -- so that is what a correction has to write. The
   * placement's status has its own control below and is what the column reads
   * once one exists.
   */
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [interviewStatus, setInterviewStatus] = useState(row.interviewStatus ?? '');
  const [candidateDoj, setCandidateDoj] = useState(row.candidateDateOfJoining ?? '');
  const [appliedPosition, setAppliedPosition] = useState(row.position ?? '');

  /** Whether the placement half of this dialog has anything to write to. */
  const hasPlacement = Boolean(row.placementId);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(
            // Null where there is no placement to write to: an admin
            // correcting a row that never reached one is editing the
            // candidate and the submission, and a PATCH to a placement that
            // does not exist is a 404 dressed up as a save.
            !hasPlacement ? null : {
              position: orNull(position),
              location: orNull(location),
              dateOfInterview: orNull(dateOfInterview),
              dateOfJoining: orNull(dateOfJoining),
              ctcAnnual: orNull(ctcAnnual),
              billingAmount: orNull(billingAmount),
              feeBasis: feeBasis || null,
              feePercent: orNull(feePercent),
              guaranteeUntil: orNull(guaranteeUntil),
              // The placement's own note, where there is a placement.
              remarks: orNull(whyLeft),
              clientEmployeeRef: orNull(employeeRef),
              status,
            },
            // Sent separately, and only when it actually changed — the note
            // lives on the submission and has its own route, so an untouched
            // dialog stays a single request.
            {
              changed:
                canEditRemarks && remarks.trim() !== (row.trackerRemarks ?? ''),
              value: remarks.trim(),
            },
            // The records beside the placement. Each is null unless this
            // viewer may write it and something actually changed, so the
            // ordinary case stays one request.
            {
              candidate: !mayEditAll
                || (interviewStatus === (row.interviewStatus ?? '')
                  && candidateDoj === (row.candidateDateOfJoining ?? ''))
                ? null
                : {
                  interviewStatus: interviewStatus || null,
                  dateOfJoining: candidateDoj || null,
                },
              submission: !mayEditAll
                || (appliedPosition.trim() === (row.position ?? '')
                  && clientId === row.clientId
                  && submissionStatus === ''
                  && (hasPlacement
                    || whyLeft.trim() === (row.submissionRemarks ?? '')))
                ? null
                : {
                  ...(appliedPosition.trim() !== (row.position ?? '')
                    ? { appliedPosition: orNull(appliedPosition) }
                    : {}),
                  // Only when it moved. Sending the same client back would
                  // trip the invoice guard on a row nobody was moving.
                  ...(clientId !== row.clientId ? { clientId } : {}),
                  ...(submissionStatus ? { status: submissionStatus } : {}),
                  // Why they left goes here when there is no placement to
                  // hold it. Both columns belong to the recruiter; the row
                  // reads the placement's first.
                  ...(!hasPlacement && whyLeft.trim() !== (row.submissionRemarks ?? '')
                    ? { remarks: orNull(whyLeft) }
                    : {}),
                },
              // Only when it actually moved, and only from somebody who may
              // write it. Compared against the assertion rather than the
              // effective state for the reason the seed above gives: an
              // untouched dialog must not freeze a derived answer.
              payment:
                !mayEditAll || !hasPlacement
                  || paymentState === (row.paymentStateOverride ?? '')
                  ? null
                  : { value: paymentState || null },
            },
          );
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          {titleCaseName(row.candidateName) ?? 'Placement'}
          {row.clientName && (
            <span className="ml-1.5 font-normal text-gray-500">
              at {row.clientName}
            </span>
          )}
        </h3>
        {/* A tracker row is four records read as one -- the candidate, the
            submission, the offer and the placement -- so the dialog is
            grouped by which record a field belongs to rather than by what it
            looks like. The Placement section is simply absent on a row that
            has none, which is what its heading is for: the reader can see
            which records they are editing without being told in a paragraph. */}
        {mayEditAll && (
          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              The Submission
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label" htmlFor="tr-client">Client</label>
                {/* Every client they have, through the shared picker, with
                    this row's own client kept on the list whatever its
                    status -- a select that cannot render its own value saves
                    an empty one back.

                    Moving a row is the one edit here with consequences
                    beyond the field: the requirement link is dropped, the
                    placement moves with it, and the server refuses outright
                    once an invoice exists. Said below rather than discovered
                    through a 409. */}
                <ClientSelect
                  purpose="work"
                  id="tr-client"
                  value={clientId}
                  attachedId={row.clientId}
                  onChange={setClientId}
                />
                {clientId !== row.clientId && (
                  <p className="mt-1 text-[11px] text-amber-700">
                    Moving this row to another client also clears its
                    requirement link and moves the placement.
                    {row.paymentState !== 'not_invoiced'
                      && ' This one has been invoiced, so the server will refuse it — credit the invoice first.'}
                  </p>
                )}
              </div>

              {/* Only where there is no placement. With one, the Status
                  control further down is what the column reads, and two
                  status boxes in one dialog is a question with two answers. */}
              {!hasPlacement && (
                <div>
                  <label className="label" htmlFor="tr-substatus">Status</label>
                  <select
                    id="tr-substatus"
                    className="input"
                    value={submissionStatus || row.status}
                    onChange={(e) => setSubmissionStatus(e.target.value)}
                  >
                    {SETTABLE_SUBMISSION_STATUSES.map((value) => (
                      <option key={value} value={value}>{label(value)}</option>
                    ))}
                  </select>
  
                </div>
              )}

              <div>
                <label className="label" htmlFor="tr-intstatus">
                  Interview Status
                </label>
                {/* The recruiter's own status, in the client's words -- and
                    the field that brings a row onto this screen in the first
                    place. Correcting it here is how an admin takes one off
                    again. */}
                <SearchSelect
                  id="tr-intstatus"
                  value={interviewStatus}
                  onChange={setInterviewStatus}
                  options={INTERVIEW_STATUSES}
                />
              </div>
              <div>
                <label className="label" htmlFor="tr-cdoj">
                  Joining Date (Candidate)
                </label>
                {/* The date the recruiter was given, on the candidate. Not the
                    placement's, which is the billing record and has its own
                    box below where one exists. */}
                <input
                  id="tr-cdoj"
                  type="date"
                  className="input"
                  value={candidateDoj}
                  onChange={(e) => setCandidateDoj(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label" htmlFor="tr-applied">
                  Applied Position (Submission)
                </label>
                <input
                  id="tr-applied"
                  spellCheck={false}
                  className="input"
                  maxLength={160}
                  value={appliedPosition}
                  onChange={(e) => setAppliedPosition(e.target.value)}
                />

              </div>
            </div>
          </section>
        )}

        {/* The placement's own fields. Hidden rather than disabled where there
            is no placement: every one of them would be writing to a record
            that does not exist. */}
        {hasPlacement && (
        <section>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            The Placement
          </h4>
          <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600">
            Position
            <input
              spellCheck={false}
              className="input mt-1"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              maxLength={160}
            />
          </label>

          <label className="text-xs text-gray-600">
            Location
            <input
              spellCheck={false}
              className="input mt-1"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={120}
              placeholder="Hyderabad"
            />
          </label>

          <label className="text-xs text-gray-600">
            DOI
            <input
              className="input mt-1"
              type="date"
              value={dateOfInterview}
              onChange={(e) => setDateOfInterview(e.target.value)}
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Date of interview
            </span>
          </label>

          <label className="text-xs text-gray-600">
            DOJ
            <input
              className="input mt-1"
              type="date"
              value={dateOfJoining}
              onChange={(e) => setDateOfJoining(e.target.value)}
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Date of joining
            </span>
          </label>

          <label className="text-xs text-gray-600">
            CTC
            <input
              spellCheck={false}
              className="input mt-1"
              inputMode="decimal"
              value={ctcAnnual}
              onChange={(e) => setCtcAnnual(e.target.value)}
              placeholder="450000"
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Annual, in rupees
            </span>
          </label>

          {/* The agreed fee.
              Accepting an offer leaves it unset on purpose — the posting's
              rate is what was advertised, not what was agreed — and until
              this box existed nothing could fill it in afterwards. That one
              blank is what makes a row show no incentive (4% of this) and
              show no Raise button (billing refuses with "No agreed fee on the
              placement"), so it is the field that quietly stops the chain. */}
          <label className="text-xs text-gray-600">
            Agreed Fee
            <input
              spellCheck={false}
              className="input mt-1"
              inputMode="decimal"
              value={billingAmount}
              onChange={(e) => setBillingAmount(e.target.value)}
              placeholder="21000"
            />
            {/* Offered, not applied. One click puts it in the box; Save is
                what makes it the agreed fee. */}
            {suggestedFee !== null ? (
              <button
                type="button"
                className="mt-1 text-left text-[11px] text-indigo-600 hover:underline"
                onClick={() => setBillingAmount(suggestedFee)}
              >
                Use {money(suggestedFee)} — this client&rsquo;s agreed{' '}
                {client.data?.defaultFeeBasis === 'flat'
                  ? 'flat fee'
                  : `${client.data?.defaultFeePercent}% of CTC`}
              </button>
            ) : (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                What the client pays, in rupees. The incentive is 4% of this.
              </span>
            )}
          </label>

          <label className="text-xs text-gray-600">
            Fee Basis
            <select
              className="input mt-1"
              value={feeBasis}
              onChange={(e) => setFeeBasis(e.target.value as FeeBasis | '')}
            >
              <option value="">Not recorded</option>
              <option value="flat">Flat</option>
              <option value="ctc_percent">% of CTC</option>
            </select>
            <span className="mt-0.5 block text-[11px] text-gray-400">
              How the fee was reached. Recorded, not applied.
            </span>
          </label>

          {/* Only meaningful alongside a percentage basis, and hidden rather
              than disabled on a flat fee: a box that cannot affect anything is
              a question somebody has to work out the answer to. */}
          {feeBasis === 'ctc_percent' && (
            <label className="text-xs text-gray-600">
              Fee %
              <input
                spellCheck={false}
                className="input mt-1"
                inputMode="decimal"
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value)}
                placeholder="8.33"
              />
              <span className="mt-0.5 block text-[11px] text-gray-400">
                Explains the fee above; it does not calculate it.
              </span>
            </label>
          )}

          {/* The window a drop inside voids the fee in.
              The invoice form has warned on this since it was written; until
              the client carried a replacement period, nothing ever set it. */}
          <label className="text-xs text-gray-600">
            Guarantee Until
            <input
              className="input mt-1"
              type="date"
              value={guaranteeUntil}
              onChange={(e) => setGuaranteeUntil(e.target.value)}
            />
            {suggestedGuarantee !== null ? (
              <button
                type="button"
                className="mt-1 text-left text-[11px] text-indigo-600 hover:underline"
                onClick={() => setGuaranteeUntil(suggestedGuarantee)}
              >
                Use {suggestedGuarantee} — {client.data?.defaultReplacementDays} days
                from joining
              </button>
            ) : (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                Replacement guarantee window.
              </span>
            )}
          </label>

          <label className="text-xs text-gray-600">
            Status
            <select
              className="input mt-1"
              value={status}
              onChange={(e) => setStatus(e.target.value as PlacementStatus)}
            >
              {PLACEMENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </select>
          </label>

          {/* What happened to the fee, said by somebody rather than derived.

              The column beside it normally reads the invoice, and for most
              rows on this screen there is no invoice to read: the importer
              writes placements and no invoices, so without this box the one
              thing the business chases on says "not invoiced" for every
              imported row and can never say anything else.

              Blank is a real choice and the default, not an empty state. It
              means "read the invoice", which is what every row does until
              somebody rules on it, and it is the only way back once they have.

              Admin and owner only, matching the supervisor's note below. The
              rest of this section is open to whoever may correct the row, and
              this is the field that moves money. */}
          {mayEditAll && (
            <label className="text-xs text-gray-600">
              Payment
              <select
                className="input mt-1"
                value={paymentState}
                onChange={(e) =>
                  setPaymentState(e.target.value as PaymentState | '')
                }
              >
                <option value="">
                  {/* Named by what it would show rather than left blank, so
                      the fallback is legible without clearing the box to find
                      out. `paymentStateDerived` and not `paymentState`: on an
                      overridden row the latter is the assertion itself, which
                      would have this option offer to fall back to the answer
                      it is offering to replace. */}
                  Read the invoice —{' '}
                  {PAYMENT_CHOICE_LABEL[row.paymentStateDerived].toLowerCase()}
                </option>
                {SETTABLE_PAYMENT_STATES.map((value) => (
                  <option key={value} value={value}>
                    {PAYMENT_CHOICE_LABEL[value]}
                  </option>
                ))}
              </select>
              <span className="mt-0.5 block text-[11px] text-gray-400">
                {paymentState
                  ? 'Your answer, shown on the row instead of the invoice’s.'
                  : 'Taken from this placement’s invoice.'}
              </span>
            </label>
          )}

          {/* The client's employee number for this person.
              Theirs, not ours -- Placement.employee_id is a foreign key into
              our own employees table for somebody we later hired. This is a
              string the client assigns after joining, and it prints on the
              invoice beside the candidate's name. Recorded here because the
              recruiter is the one who learns it, on a follow-up call. */}
          <label className="text-xs text-gray-600">
            Employee ID
            <input
              spellCheck={false}
              className="input mt-1"
              maxLength={60}
              value={employeeRef}
              onChange={(e) => setEmployeeRef(e.target.value)}
              placeholder="1MGHC7866"
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              The client&rsquo;s number for them. Prints on the invoice.
            </span>
          </label>

          </div>
        </section>
        )}

        {/* Why they left, in the recruiter's words, and always available.
            It used to live inside the placement block, which meant the one
            fact the client asks a recruiter to record was missing from every
            row that never reached a placement -- including every row marked
            Drop by hand. It writes to the placement where there is one and to
            the submission's own remark where there is not; both are columns
            the recruiter owns, and the row prefers the placement's. */}
        <section>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Notes
          </h4>
          <label className="block text-xs text-gray-600">
            Why they left
            <textarea
              spellCheck={true}
              className="input mt-1 block w-full"
              rows={2}
              maxLength={2000}
              value={whyLeft}
              onChange={(e) => setWhyLeft(e.target.value)}
              placeholder="Left for a closer job, salary, family reasons…"
            />
          </label>

          <label className="mt-3 block text-xs text-gray-600">
            Remarks
            <textarea
              spellCheck={true}
              className="input mt-1 block w-full"
              rows={2}
              maxLength={2000}
              value={remarks}
              readOnly={!canEditRemarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={
                canEditRemarks ? "The supervisor's note on this row" : ''
              }
            />
            {/* Shown rather than hidden for a recruiter: the note is about
                their row and worth reading, and a field that silently vanished
                by rank would leave them wondering what a manager could see. */}
            {!canEditRemarks && (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                Your manager writes this.
              </span>
            )}
          </label>
        </section>

        {/* Said before the save, not after: the sheet's wording is the only
            record of what a row like "Working/Drop" originally read. */}
        {statusChanged && row.statusRaw && (
          <p className="rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            This row still reads <strong>{row.statusRaw}</strong> from the
            tracker sheet. Changing the status drops that wording, because it
            would otherwise caption a status it no longer describes.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * The supervisor's note, edited where it is read.
 *
 * In place rather than in a dialog: it is one short field on a wide table, and
 * the value of a follow-up note is that it can be written while looking at the
 * row it is about.
 *
 * Escape cancels and Enter saves, because a note is one line often enough that
 * reaching for the mouse to confirm it is friction. Shift+Enter still breaks a
 * line, so a longer note is not blocked by that shortcut.
 */
function RemarksCell({
  row,
  editable,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
}: {
  row: TrackerRow;
  editable: boolean;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (note: string) => void;
}) {
  const [draft, setDraft] = useState(row.trackerRemarks ?? '');

  if (!editing) {
    if (!editable) {
      return (
        <span title={row.trackerRemarks ?? undefined}>
          {row.trackerRemarks ?? <Blank />}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="w-full truncate text-left hover:text-gray-900 hover:underline"
        onClick={onEdit}
        title={row.trackerRemarks ?? 'Add a follow-up note'}
      >
        {row.trackerRemarks ?? <Blank />}
      </button>
    );
  }

  return (
    <div className="space-y-1">
      <textarea
        spellCheck={true}
        className="input w-full text-xs"
        rows={2}
        autoFocus
        value={draft}
        maxLength={2000}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSave(draft);
          }
        }}
      />
      <div className="flex gap-1.5">
        <button
          type="button"
          className="btn-primary px-2 py-0.5 text-[11px]"
          disabled={saving}
          onClick={() => onSave(draft)}
        >
          Save
        </button>
        <button
          type="button"
          className="btn-ghost px-2 py-0.5 text-[11px]"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function TrackerPage() {
  const queryClient = useQueryClient();
  const me = useAuth((state) => state.user);
  const [editing, setEditing] = useState<TrackerRow | null>(null);
  const [editingRemarks, setEditingRemarks] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  /**
   * Manager and above, and now purely a matter of what is worth showing.
   *
   * It used to match the server's Approver on the remarks route and gate that
   * edit; the remarks are admin-only now, so nothing this flag controls
   * writes anything. What is left is the recruiter filter and the export,
   * both of which a manager keeps: a recruiter is scoped to their own rows
   * anyway, so the dropdown would hold one entry that cannot change anything.
   *
   * Reading the screen and changing it are deliberately different tiers. A
   * manager still sees their subtree's rows.
   */
  const supervisor = isSupervisor(me);
  /**
   * Company Admin and Super Admin. The only people who may change this screen.
   *
   * Named for what it grants rather than for the roles it holds, because the
   * same flag now does five things: it shows the edit button at all, it
   * reveals the fields that live outside the placement, it decides whether
   * those fields are sent, it gates the supervisor's note, and it gates the
   * payment status.
   *
   * The button used to appear for anybody on a row that had a placement --
   * which is every row that carries money. The endpoints behind the dialog
   * refuse a manager and a recruiter now; this is so neither meets that
   * refusal as a 403 on a form they had already filled in.
   */
  const privileged = isCompanyAdmin(me);

  const [recruiterId, setRecruiterId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [interviewStatusFilter, setInterviewStatusFilter] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  /**
   * Narrow to the rows this person is the recruiter on.
   *
   * The server has taken ``mine_only`` on this route all along -- _scoped
   * swaps the subtree for the caller's own id -- and nothing on the screen
   * ever set it. A supervisor could reach their own rows only by picking
   * themselves out of the recruiter dropdown, which is the same question
   * asked the long way round.
   *
   * Offered to everyone rather than to supervisors, unlike the recruiter
   * dropdown beside it. For a recruiter apply_owner_scope has already pinned
   * the page to their own rows, so this changes nothing for them -- but a
   * control that is present and agrees with what they are seeing is easier to
   * reason about than one that appears for some people and not others.
   */
  const [mineOnly, setMineOnly] = useState(false);
  /**
   * Where in the book we are.
   *
   * The list asked for 200 rows and said "Showing 200 of 2,301. Narrow by
   * recruiter, status or month to see the rest" — which is a filter offered
   * in place of a way through, and no combination of them reaches row 201.
   */
  const [offset, setOffset] = useState(0);

  /**
   * Setting a filter always returns to the first page.
   *
   * Wrapped once rather than remembered at four call sites — the one that got
   * forgotten would be the bug, and it would look like "the filter found
   * nothing" rather than "you are past the end". Same shape as the calls
   * report in 065f104, which has the same four filters.
   */
  function filterSetter(set: (value: string) => void) {
    return (value: string) => {
      set(value);
      setOffset(0);
    };
  }

  /**
   * Everyone who could be named as a recruiter on a submission.
   *
   * Not filtered to `role=recruiter`: a manager or company admin who makes a
   * submission is its recruiter too, and filtering the dropdown by role would
   * silently omit them — the filter would then hide rows it should show.
   * Plain employees are dropped because they never own one.
   *
   * Inactive accounts included, because a recruiter who has left is still the
   * recruiter on the rows they made. Historical accounts created for imported
   * sheets are deactivated by design, and without this their rows would be
   * reachable only by clearing the filter entirely.
   */
  const tracker = useQuery<Page<TrackerRow>>({
    // The acting company is not in the key — the request interceptor puts it on
    // the call, and switching company resets this cache at the picker.
    queryKey: [
      'tracker', recruiterId, statusFilter, interviewStatusFilter,
      year, month, mineOnly, offset,
    ],
    queryFn: async () =>
      (
        await api.get('/api/v1/tracker', {
          params: {
            limit: PAGE_SIZE,
            offset,
            ...(recruiterId ? { recruiterId } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(interviewStatusFilter
              ? { interviewStatus: interviewStatusFilter }
              : {}),
            ...(year ? { year: Number(year) } : {}),
            ...(month ? { month: Number(month) } : {}),
            ...(mineOnly ? { mineOnly: true } : {}),
          },
        })
      ).data,
  });

  /**
   * The rows on screen, as a spreadsheet.
   *
   * Sends the same four filters the list query above sends, from the same
   * state, so a filtered view downloads as the filtered rows. The status one
   * matters most: the tracker's Status is derived across submission,
   * interview, offer and placement, and the server filters the export on that
   * same expression rather than on the stored submission status.
   */
  const exportTracker = useMutation({
    mutationFn: async () =>
      downloadExport({
        kind: 'submissions',
        format: 'xlsx',
        params: {
          ...(recruiterId ? { recruiterId } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(interviewStatusFilter
            ? { interviewStatus: interviewStatusFilter }
            : {}),
          ...(year ? { year: Number(year) } : {}),
          ...(month ? { month: Number(month) } : {}),
        },
      }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });

  /** Both writes land on the same screen, so both invalidate the same keys. */
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tracker'] });
    // Prefix match, so Placements picks the row up as well as this screen.
    void queryClient.invalidateQueries({ queryKey: ['placements'] });
  };

  /**
   * The dialog saves to two places, because Remarks is not a placement field.
   *
   * Sequential and in this order: the placement fields are the substance of
   * the correction, the note is a comment on it. If the second call fails the
   * first has still landed, and saying so beats a single "Saved" that is
   * mostly true — a rollback across two endpoints is not something this pair
   * can honestly offer.
   *
   * The remarks call is skipped entirely when the text did not change, so the
   * ordinary edit stays one request.
   */
  const savePlacement = useMutation({
    mutationFn: async ({
      placementId,
      submissionId,
      candidateId,
      payload,
      remarks,
      extras,
    }: {
      placementId: string | null;
      submissionId: string;
      candidateId: string;
      payload: Record<string, unknown> | null;
      remarks: { changed: boolean; value: string };
      extras: {
        candidate: Record<string, unknown> | null;
        submission: Record<string, unknown> | null;
        payment: { value: PaymentState | null } | null;
      };
    }) => {
      /**
       * Up to five requests, one per record, and only for what changed.
       *
       * A tracker row is assembled from the candidate, the submission, the
       * offer and the placement, so correcting it in full is several writes
       * rather than one. They run in sequence rather than in parallel on
       * purpose: if the second fails, the message can say which half landed,
       * and a half-applied row somebody knows about is recoverable while a
       * half-applied row reported as a success is not.
       *
       * Ordered least to most consequential. The placement carries the money
       * and goes late, so a fee that did save is not sitting behind a status
       * that did not.
       *
       * Payment is its own request after the placement rather than a field on
       * it, because it is its own route with its own gate -- the placement
       * PATCH is open to whoever can see the row, and this one is admin and
       * owner. It goes before the remarks so that the money is settled before
       * the note about it.
       */
      if (extras.candidate) {
        await api.patch(`/api/v1/candidates/${candidateId}`, extras.candidate);
      }
      if (extras.submission) {
        try {
          await api.patch(`/api/v1/submissions/${submissionId}`, extras.submission);
        } catch (error) {
          throw new Error(
            `The candidate was updated, but the submission was not: ${errorMessage(error)}`,
          );
        }
      }
      if (payload && placementId) {
        try {
          await api.patch(`/api/v1/placements/${placementId}`, payload);
        } catch (error) {
          throw new Error(
            `The candidate and submission were updated, but the placement was not: ${errorMessage(error)}`,
          );
        }
      }
      if (extras.payment) {
        try {
          await api.patch(`/api/v1/tracker/${submissionId}/payment`, {
            paymentState: extras.payment.value,
          });
        } catch (error) {
          throw new Error(
            `Row saved, but the payment status was not: ${errorMessage(error)}`,
          );
        }
      }
      if (remarks.changed) {
        try {
          await api.patch(`/api/v1/tracker/${submissionId}/remarks`, {
            trackerRemarks: remarks.value || null,
          });
        } catch (error) {
          throw new Error(
            `Row saved, but the remarks could not be updated: ${errorMessage(error)}`,
          );
        }
      }
    },
    onSuccess: () => {
      invalidate();
      setEditing(null);
      toast.success('Saved');
    },
    onError: (error) => {
      // The placement half may well have landed, so the table is refreshed
      // either way rather than left showing values the server no longer holds.
      invalidate();
      toast.error(errorMessage(error));
    },
  });

  const saveRemarks = useMutation({
    mutationFn: async ({
      submissionId,
      note,
    }: {
      submissionId: string;
      note: string;
    }) =>
      (
        await api.patch(`/api/v1/tracker/${submissionId}/remarks`, {
          trackerRemarks: note,
        })
      ).data,
    onSuccess: () => {
      invalidate();
      setEditingRemarks(null);
      toast.success('Note saved');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = tracker.data?.items ?? [];
  const total = tracker.data?.total ?? 0;
  const filtered = Boolean(
    recruiterId || statusFilter || interviewStatusFilter || year || month || mineOnly,
  );

  return (
    <div className="space-y-6">
      <section className="card border-2 border-indigo-100 shadow-sm [&_label]:text-sm [&_label]:font-semibold [&_label]:text-gray-700">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900">Tracker</h2>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          One row per submission, from the day a candidate went to a client
          through to whether they are still there. Status is read across the
          interview, offer and placement behind each row, so it moves on its
          own as the pipeline does.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          {supervisor && (
            <RecruiterFilter
              value={recruiterId}
              onChange={filterSetter(setRecruiterId)}
            />
          )}

          <label className="text-xs text-gray-600">
            Status
            <select
              className="input mt-1 py-1 text-xs"
              value={statusFilter}
              onChange={(e) => filterSetter(setStatusFilter)(e.target.value)}
            >
              <option value="">Any Status</option>
              {TRACKER_STATUSES.map((value) => (
                <option key={value} value={value}>{label(value)}</option>
              ))}
            </select>
          </label>

          {/* The recruiter's own status, which is what somebody asking "who
              has joined" is actually narrowing on. Only the statuses that put
              a row on this screen are offered: filtering the Joining Tracker
              by "1st Round" would always return nothing, and an option that
              cannot match is a control that looks broken. */}
          <label className="text-xs text-gray-600">
            Interview Status
            <select
              className="input mt-1 py-1 text-xs"
              value={interviewStatusFilter}
              onChange={(e) => filterSetter(setInterviewStatusFilter)(e.target.value)}
            >
              <option value="">Any</option>
              {INTERVIEW_STATUSES.filter((option) =>
                TRACKER_INTERVIEW_STATUSES.includes(option.value),
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Year
            <select
              className="input mt-1 py-1 text-xs"
              value={year}
              onChange={(e) => filterSetter(setYear)(e.target.value)}
            >
              <option value="">Any Year</option>
              {YEARS.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Month
            <select
              className="input mt-1 py-1 text-xs"
              value={month}
              onChange={(e) => filterSetter(setMonth)(e.target.value)}
            >
              <option value="">Any Month</option>
              {MONTHS.map((name, index) => (
                <option key={name} value={String(index + 1)}>{name}</option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 pb-1 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={mineOnly}
              onChange={(e) => {
                setMineOnly(e.target.checked);
                setOffset(0);
              }}
            />
            Only Mine
          </label>

          {filtered && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setRecruiterId('');
                setStatusFilter('');
                setInterviewStatusFilter('');
                setYear('');
                setMonth('');
                setMineOnly(false);
                // Back to the first page, like every other filter change.
                //
                // This is the call site filterSetter was wrapped to protect,
                // and the one that got missed -- it calls the raw setters, so
                // it never reset the offset. Clearing the filters from page
                // five left you on page five of the unfiltered list, which
                // reads as "clearing the filters lost my rows" rather than as
                // a paging position nobody moved.
                setOffset(0);
              }}
            >
              Clear filters
            </button>
          )}

          {/* Beside the filters rather than in the page header, because what
              it downloads is whatever they currently say. */}
          {supervisor && (
            <button
              type="button"
              className="btn-ghost ml-auto text-xs"
              disabled={exportTracker.isPending}
              onClick={() => exportTracker.mutate()}
              title="Downloads the rows matching the filters above"
            >
              {exportTracker.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-3.5 w-3.5" />
              )}
              Excel
            </button>
          )}

          {isCompanyAdmin(me) && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setImporting(true)}
              title="Import a Joining Tracker workbook"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Import Excel
            </button>
          )}
        </div>

        {/* Month means the submission date, which is not the joining month.
            Worth saying on screen: a monthly count here will not match the
            billing month for the same candidate. */}
        {(year || month) && (
          <p className="mt-2 text-xs text-gray-400">
            Filed by the date the candidate was submitted, not the date they
            joined — so somebody submitted in February and joined in March is
            in February.
          </p>
        )}
      </section>


      {importing && (
        <ImportPanel
          onClose={() => setImporting(false)}
          title="Import a Joining Tracker Workbook"
        />
      )}
      {tracker.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : tracker.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">
            Could not load the tracker. This is a failure to fetch, not an empty
            sheet.
          </p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void tracker.refetch()}
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <Table2 className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            {filtered
              ? 'No rows match these filters.'
              : 'Nothing submitted yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-scroll rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  {/* Sr.No is now the first column */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Serial Number">Sr.No</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Candidate</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Recruiter</th>
                  {/* The month this row files under, read off the same
                      submission date the year/month filter narrows on. Not
                      the joining month -- see lib/trackerColumns for why the
                      two must not disagree. */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="From the submission date, which is what the month filter uses">
                    Month
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Mobile</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Client</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Job Location</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Designation</th>
                  <th
                    scope="col"
                    className="px-4 py-2.5 font-medium"
                    title="Date of Joining. From the placement, or the candidate record where no placement exists yet — those are marked with an asterisk."
                  >
                    DOJ
                  </th>
                  {/* Two CTC figures, because they are two facts. This one
                      is the offer letter's, which is what the client asked
                      for; the next is what they actually joined on, and they
                      diverge whenever a number is renegotiated between the
                      two. Blank on every row that came from the workbook —
                      the importer records no offers. */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Annual CTC on the offer letter. Blank where no offer was recorded.">
                    Offered CTC
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Annual CTC on joining — what the edit dialog sets">CTC</th>
                  {/* Derived from the agreed fee, never typed.
                      Called recruiter_commission on the server, and
                      recruiterCommission on the row — the rename is the
                      business's word for it and stops at the display. See
                      that function for why the base is the fee and not the
                      CTC or the amount received. */}
                  {/* Both were already on the row and visible only inside the
                      edit dialog, which meant the figure the incentive is
                      calculated from could not be read off the screen it is
                      shown on. */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="How the fee was reached. Recorded, not applied.">
                    Sourcing Fee
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium" title="The agreed fee — what the client pays for this placement">
                    Billing Amount
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium" title="4% of the Agreed Fee">
                    Incentive
                  </th>
                  {/* The ledger, items 15 and 16. Distinct from Incentive
                      above, which is 4% of whatever the fee says today —
                      these are what was accrued and earned, with a dated
                      row behind every movement. */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Accrued but not yet earned. Becomes earned ninety days after joining.">
                    {TENTATIVE_INCENTIVE_LABEL}
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Ninety days complete. Paid with the following month’s salary.">
                    {EARNED_INCENTIVE_LABEL}
                  </th>
                  {/* Two status columns, because they answer two
                      questions: this one is what the recruiter recorded by
                      hand in the client's own vocabulary, the next is what
                      the pipeline derives from the placement, the offer and
                      the submission. They are allowed to disagree. */}
                  <th
                    scope="col"
                    className="px-4 py-2.5 font-medium"
                    title="What the recruiter recorded on the candidate. Marking somebody Joined & Working there is what brings them onto this screen."
                  >
                    Interview Status
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Payment</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Why they left</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Remarks</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => (
                  <tr key={row.submissionId} className={ROW_TONE[row.status]}>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-400">
                      {offset + index + 1}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {titleCaseName(row.candidateName) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {titleCaseName(row.recruiterName) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {monthLabel(row.submittedOn) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {formatPhone(row.candidatePhone) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {row.clientName ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {row.location ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {row.position ?? <Blank />}
                    </td>
                    {/* The placement's date where there is one, because that
                        is what bills and what the guarantee counts from, and
                        the recruiter's own where there is not. The second is
                        marked, so a reader can tell a billing record from
                        somebody's note -- the two can disagree, and the
                        placement is the one that wins. */}
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {row.dateOfJoining ? (
                        formatDayMonthYear(row.dateOfJoining)
                      ) : row.candidateDateOfJoining ? (
                        <span
                          className="text-gray-500"
                          title="From the candidate record. No placement has been created yet."
                        >
                          {formatDayMonthYear(row.candidateDateOfJoining)}*
                        </span>
                      ) : (
                        <Blank />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-900">
                      {row.offeredCtc ? money(row.offeredCtc) : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-900">
                      {row.ctcAnnual ? money(row.ctcAnnual) : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {sourcingFeeLabel(row.feeBasis, row.feePercent) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {/* Blank rather than zero on an unagreed fee, same as
                          the incentive beside it: nothing has been agreed,
                          and 0.00 would say something was. */}
                      {row.billingAmount ? money(row.billingAmount) : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {/* Blank, not zero, where no fee has been agreed. A
                          0.00 would assert an incentive of nothing rather
                          than the absence of one. */}
                      {row.recruiterCommission ? (
                        money(row.recruiterCommission)
                      ) : (
                        <Blank />
                      )}
                    </td>
                    {/* Blank rather than zero, like the fee columns. A
                        column of zeroes on every candidate trains people
                        to stop reading it; the My Day tiles show the
                        number, because there the zero is the answer. */}
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {hasValue(row.incentiveTentative)
                        ? money(row.incentiveTentative)
                        : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums font-medium text-gray-900">
                      {hasValue(row.incentiveEarned)
                        ? money(row.incentiveEarned)
                        : <Blank />}
                    </td>
                    {/* The recruiter's own status, in the client's words.
                        Its own column rather than replacing the one beside it:
                        that one is derived from the placement, the offer and
                        the submission, and a screen showing only the typed
                        value would lose the answer to "what has the client
                        actually decided". */}
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                      {interviewStatusLabel(row.interviewStatus) ?? <Blank />}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-2.5 font-medium ${STATUS_TEXT[row.status]}`}
                    >
                      {/* What the sheet wrote, when it wrote something.
                          "Working/Drop" normalises to `drop` because a drop
                          outranks working, so showing only the enum would
                          rewrite a real row into something nobody typed. The
                          colour still comes from the enum. */}
                      {row.statusRaw ?? label(row.status)}
                    </td>
                    {/* A simplified reading of the invoice, not a second
                        status. The real draft/raised/partially paid/paid
                        vocabulary is untouched and lives on the invoice
                        screens; this answers "did the money land" for
                        somebody who does not work in billing.

                        Not invoiced is deliberately its own state and reads
                        grey, not amber: nothing is owed on a placement nobody
                        has billed for, and showing it as awaited would have
                        the tracker assert a receivable that does not exist. */}
                    <td className="whitespace-nowrap px-4 py-2.5">
                      {/* Ringed where somebody said so rather than where the
                          invoice did. An asserted badge that looks identical
                          to a computed one is the version of this that does
                          harm: the answer moves and nothing says why. */}
                      <span
                        className={`badge ${PAYMENT_TONE[row.paymentState]} ${
                          row.paymentStateOverride ? PAYMENT_OVERRIDDEN_RING : ''
                        }`}
                        title={
                          row.paymentStateOverride
                            ? 'Set by an administrator, not read from an invoice'
                            : undefined
                        }
                      >
                        {/* An assertion is captioned in the words it was
                            chosen in, a derivation in the invoice's.

                            The two vocabularies genuinely differ, and this is
                            not a tidy-up waiting to happen: `yet_to_receive`
                            reads "Invoice done" when it came off a raised
                            invoice, which is the useful thing to know about
                            one, and "Not yet paid" when somebody picked those
                            words from the dropdown. Showing the derived
                            caption on an asserted row would have the badge
                            say something the person who set it never chose. */}
                        {row.paymentStateOverride
                          ? PAYMENT_CHOICE_LABEL[row.paymentState]
                          : PAYMENT_LABEL[row.paymentState]}
                      </span>
                    </td>
                    {/* Read-only here and edited in the placement dialog.
                        Deliberately not an in-place editor like the
                        supervisor's Remarks beside it: that one is a one-line
                        note a manager drops on a row in passing, while this
                        belongs to the placement and is saved with the status
                        change that usually prompts it. */}
                    {/* The placement's note where there is one, the
                        submission's where there is not -- the same fallback
                        the dialog writes through, so the column and the box
                        that fills it cannot disagree. */}
                    <td
                      className="max-w-xs truncate px-4 py-2.5 text-gray-500"
                      title={row.placementRemarks ?? row.submissionRemarks ?? undefined}
                    >
                      {row.placementRemarks ?? row.submissionRemarks ?? <Blank />}
                    </td>
                    <td className="max-w-xs px-4 py-2.5 text-gray-500">
                      <RemarksCell
                        // Keyed by row, so the draft belongs to the row it was
                        // opened on rather than to the cell's position.
                        key={row.submissionId}
                        row={row}
                        editable={privileged}
                        editing={editingRemarks === row.submissionId}
                        saving={saveRemarks.isPending}
                        onEdit={() => setEditingRemarks(row.submissionId)}
                        onCancel={() => setEditingRemarks(null)}
                        onSave={(note) =>
                          saveRemarks.mutate({
                            submissionId: row.submissionId,
                            note,
                          })
                        }
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {/* Absent, not disabled, on a row with no placement --
                          every commercial field in the dialog lives there, so
                          there would be nothing to write to. An administrator
                          gets it regardless: the interview status and joining
                          date that brought such a row onto this screen are on
                          the candidate, and correcting them is the only way to
                          take a wrongly-marked row off it. */}
                      {/* Admin and owner only. This read
                          `row.placementId || privileged`, which showed the
                          pencil to a recruiter and a manager on every row that
                          had a placement -- and the placement is the billing
                          record. The endpoints behind the dialog refuse them
                          now; this is so nobody meets that as a 403. */}
                      {privileged && (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => setEditing(row)}
                          title="Correct This Row"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="sr-only">
                            Edit {titleCaseName(row.candidateName) ?? 'placement'}
                          </span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Which rows these are, not just how many exist. The numbering
              in column one counts through the pages, so it agrees with this. */}
          <p className="text-xs text-gray-400">
            {total === 0
              ? 'No rows.'
              : `${(offset + 1).toLocaleString('en-IN')}\u2013${Math.min(
                offset + rows.length,
                total,
              ).toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}.`}
          </p>

          {/* Offered whenever there is more than one page, including on the
              last one — a Previous that vanishes at the end strands whoever
              paged there. Both buttons disable at their edge rather than
              disappearing, so the control does not move under the cursor. */}
          {total > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Page {Math.floor(offset / PAGE_SIZE) + 1} of{' '}
                {Math.ceil(total / PAGE_SIZE).toLocaleString('en-IN')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Opens on a row with no placement too, for an administrator: the
          fields that brought it here live on the candidate, and a row nobody
          can correct from the screen that shows it is a row that stays
          wrong. Everybody else keeps what they had -- placement rows only. */}
      {editing && privileged && (
        <EditDialog
          // Keyed by row, so the fields reload instead of keeping the previous
          // row's edits when a second row is opened.
          key={editing.submissionId}
          row={editing}
          saving={savePlacement.isPending}
          canEditRemarks={privileged}
          mayEditAll={privileged}
          onCancel={() => setEditing(null)}
          onSave={(payload, remarks, extras) =>
            savePlacement.mutate({
              placementId: editing.placementId,
              submissionId: editing.submissionId,
              candidateId: editing.candidateId,
              payload,
              remarks,
              extras,
            })
          }
        />
      )}
    </div>
  );
}
